import React, { useState, useEffect } from "react";
import { Search, Filter, ChevronDown, X, MoreVertical } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { ADGetTasks } from "../Service/AllTaskService";
import ViewTaskModal from "../../../../Task/Modal/ViewTaskModal";
import ADCompletedTaskModal from "../Modal/ADCompletedTaskModal";
import ADInProgressTaskModal from "../Modal/ADInProgressTaskModal";
import ADReopenTaskModal from "../Modal/ADReopenTaskModal";
import ADHoldTaskModal from "../Modal/ADHoldTaskModal";
import ADClosedTaskModal from "../Modal/ADClosedTaskModal";
import ADResumeTaskModal from "../Modal/ADResumeTaskModal";
import TaskHistoryModal from "../../../../Task/Modal/TaskHistoryModal";
import CreateTaskModal from "../../../../Task/Modal/CreateTaskModal";
import ReassignTaskModal from "../../../../Task/Modal/ReassignTaskModal";
import ADInvalidTaskModal from "../Modal/ADInvalidTaskModal";
import NotificationAlert from "../../../../Alerts/NotificationAlert";
import DropdownPortal from "../../../../DropDownPortal";
import FilterTableModal from "../../../../Modal/FilterTableModal";
import { ChevronLeft, ChevronRight } from "lucide-react";
import Cookies from "js-cookie";
import TableHeader from "../../../../Common/TableHeader";
import Pagination from "../../../../Common/Pagination";
import { globalSearch } from "../../../../Utils/globalSearch";

const getSeverityStyle = (severity) => {
  switch (severity) {
    case "High":
      return "bg-red-100 text-red-700";
    case "Moderate":
      return "bg-yellow-100 text-yellow-700";
    case "Low":
      return "bg-green-100 text-green-700";
    default:
      return "bg-gray-100 text-gray-600";
  }
};

const ADAllTask = () => {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [filters, setFilters] = useState({});
  const [filteredTasks, setFilteredTasks] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState(null);
  const [viewModal, setViewModal] = useState(false);
  const [selectedtask, setSelectedtask] = useState(null);
  const [openActionMenu, setOpenActionMenu] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });

  // In-progress Modal States
  const [showInProgressModal, setShowInProgressModal] = useState(false);
  const [inProgressTaskData, setInProgressTaskData] = useState(null);

  //Hold Modal States
  const [showHoldModal, setShowHoldModal] = useState(false);
  const [holdTaskData, setHoldTaskData] = useState(null);

  // Completed Modal States
  const [showCompletedModal, setShowCompletedModal] = useState(false);
  const [completedTaskData, setCompletedTaskData] = useState(null);

  // Reopen Modal States
  const [showReopenModal, setShowReopenModal] = useState(false);
  const [reopenTaskData, setReopenTaskData] = useState(null);

  //Close Modal States
  const [showClosedModal, setShowClosedModal] = useState(false);
  const [closedTaskData, setClosedTaskData] = useState(null);

  //Resume Modal States
  const [showResumeModal, setShowResumeModal] = useState(false);
  const [resumeTaskData, setResumeTaskData] = useState(null);

  //Re-assign Modal States
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [reAssignTaskData, setReAssignTaskData] = useState(null);

  //Invalid Modal States
  const [showInvalidTaskModal, setShowInvalidTaskModal] = useState(false);
  const [invalidTaskData, setInvalidTaskData] = useState(null);

  // Histort Modal States
  const [historyModal, setHistoryModal] = useState(false);
  const [historyData, setHistoryData] = useState([]);

  // filtering states
  const [columnFilters, setColumnFilters] = useState({
    "Project Name": [],
    "task Type": [],
    "Assigned To": [],
    Status: [],
    Priority: [],
  });

  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [filterModalField, setFilterModalField] = useState("");
  const [filterModalOptions, setFilterModalOptions] = useState([]);

  // Search States
  const [searchQuery, setSearchQuery] = useState("");

  // Pagination States
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const [errors, setErrors] = useState({});
  const [isPM, setIsPM] = useState(false);

  const [openCreateFromCopy, setOpenCreateFromCopy] = useState(false);

  //load tasks
  useEffect(() => {
    loadTasks(0);
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, columnFilters, filteredTasks, rowsPerPage]);

  useEffect(() => {
    const handler = () => setOpenCreateFromCopy(true);
    window.addEventListener("openCreateTaskWithCopy", handler);

    return () => window.removeEventListener("openCreateTaskWithCopy", handler);
  }, []);

  const loadTasks = async () => {
    try {
      const response = await ADGetTasks(0);
      const data = response.data;

      if (data.status === "Success") {
        const formatted = data.tasks.map((t, index) => ({
          id: index + 1,
          taskNo: t.taskNo,
          description: t.description,
          severity: t.severity,
          status: t.status,
          projectName: t.projectName,
          assignedBy: t.assignedBy,
          assignedTo: t.assignedTo,
          createdAt: t.createdAt,
          estimatedComplete: t.estimatedComplete,
          lastUpdatedAt: t.lastUpdatedAt,
          ...t,
        }));

        setTasks(formatted);

        const filtered = applyFiltersAndSearch(formatted, filters, searchQuery);

        setFilteredTasks(filtered);
        setCurrentPage(1);
      }
    } catch (err) {
      if (err.response?.status === 400) {
        navigate("/bad-request", {
          state: {
            error: err.response?.data?.message || "Bad request (400)",
          },
        });
        return;
      }
      console.error("Error fetching tasks:", err);
    }
  };

  useEffect(() => {
    const pm = Cookies.get("Orbit_PMCount");
    if (pm && Number(pm) > 0) {
      setIsPM(true);
    } else {
      setIsPM(false);
    }
  }, []);

  const formatDateTime = (dateString) => {
    if (!dateString) return "--";

    const date = new Date(dateString);

    if (isNaN(date.getTime())) return "--";

    const day = String(date.getDate()).padStart(2, "0");
    const month = date.toLocaleString("en-US", { month: "short" });
    const year = date.getFullYear();

    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";

    hours = hours % 12 || 12;

    return `${day}-${month}-${year} ${hours}:${minutes} ${ampm}`;
  };

  // Close menu when clicking outside
  useEffect(() => {
    const closeMenu = () => setOpenActionMenu(null);
    window.addEventListener("click", closeMenu);
    return () => window.removeEventListener("click", closeMenu);
  }, []);

  // Filters Function
  const getUniqueOptionsForField = (field) => {
    const map = {
      "Project Name": "projectName",
      "task Type": "taskType",
      "Assigned To": "assignedTo",
      Status: "status",
      Priority: "severity",
    };

    const prop = map[field];
    if (!prop) return [];

    const set = new Set();
    tasks.forEach((t) => {
      const val = t[prop] ?? "--";
      set.add(val);
    });

    return Array.from(set).sort((a, b) => {
      if (a === "--") return 1;
      if (b === "--") return -1;
      return String(a).localeCompare(String(b));
    });
  };

  const openFilterFor = (field) => {
    const opts = getUniqueOptionsForField(field);
    setFilterModalOptions(opts);
    setFilterModalField(field);
    setFilterModalOpen(true);
  };

  const applyColumnFilter = (field, selections) => {
    setColumnFilters((prev) => ({ ...prev, [field]: selections }));
    const newFilters = { ...columnFilters, [field]: selections };

    let result = tasks;

    // Apply all column filters
    Object.entries(newFilters).forEach(([f, values]) => {
      if (!values || values.length === 0) return;

      const map = {
        "Project Name": "projectName",
        "task Type": "taskType",
        "Assigned To": "assignedTo",
        Status: "status",
        Priority: "severity",
      };

      const prop = map[f];
      result = result.filter((t) => values.includes(t[prop] ?? "--"));
    });

    // Apply search after column filters
    if (searchQuery.trim() !== "") {
      result = result.filter((t) =>
        Object.values({
          title: t.title,
          description: t.description,
          projectName: t.projectName,
          severity: t.severity,
          taskType: t.taskType,
          assignedTo: t.assignedTo,
          status: t.status,
        })
          .join(" ")
          .toLowerCase()
          .includes(searchQuery.toLowerCase()),
      );
    }

    setFilteredTasks(result);
  };

  //To Avoid filters remove after action
  const applyFiltersAndSearch = (sourceTasks, filters, searchQuery) => {
    let result = [...sourceTasks];

    // Apply Filters
    Object.keys(filters).forEach((f) => {
      if (filters[f]?.length > 0) {
        result = result.filter((row) => filters[f].includes(row[f] ?? "--"));
      }
    });
    // Apply Search
    if (searchQuery.trim() !== "") {
      const q = searchQuery.toLowerCase();
      result = result.filter((task) =>
        JSON.stringify(task).toLowerCase().includes(q),
      );
    }

    return result;
  };

  const clearAllFilters = () => {
    setColumnFilters({
      "Project Name": [],
      "task Type": [],
      "Assigned To": [],
      Status: [],
      Priority: [],
    });
    setFilteredTasks(tasks);
  };

  // Search Function
  const handleSearch = (query) => {
    setSearchQuery(query);

    const result = globalSearch(tasks, query);

    setFilteredTasks(result);
  };

  const rowsToShow =
    searchQuery.trim() !== "" ||
    Object.values(columnFilters).some((arr) => arr.length > 0)
      ? filteredTasks
      : tasks;

  // Pagination logic
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;

  const currentRows = rowsToShow.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(rowsToShow.length / rowsPerPage);

  const startIndex = rowsToShow.length === 0 ? 0 : indexOfFirstRow + 1;
  const endIndex = Math.min(indexOfLastRow, rowsToShow.length);

  return (
    <>
      <div className="p-6 bg-gray-50 min-h-screen">
        {/*  Header Section */}
        <TableHeader
          searchQuery={searchQuery}
          onSearch={handleSearch}
          onCreate={() => setShowModal(true)}
          placeholder="Search for tasks"
          createLabel="+ Create Task"
          totalRecords={rowsToShow.length}
          startIndex={startIndex}
          endIndex={endIndex}
          rowsPerPage={rowsPerPage}
          onPageSizeChange={setRowsPerPage}
        />

        {/*  Table Section */}
        <div className="overflow-x-auto overflow-visible bg-white rounded-lg shadow-sm border border-gray-200">
          <table className="w-full text-sm text-left text-gray-700">
            <thead className="bg-gray-100 text-gray-600 uppercase text-xs font-semibold">
              <tr>
                <th className="p-3">Task No.</th>

                <th className="p-3">
                  <div className="flex items-center gap-2">
                    <span>Project Name</span>
                    <button
                      onClick={() => openFilterFor("Project Name")}
                      className="p-1 rounded hover:bg-gray-100"
                      title="Filter Project Name"
                    >
                      <Filter size={14} />
                    </button>
                    {columnFilters["Project Name"]?.length > 0 && (
                      <span className="ml-1 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                        {columnFilters["Project Name"].length}
                      </span>
                    )}
                  </div>
                </th>

                <th className="p-3">
                  <div className="flex items-center gap-2">
                    <span>Assigned To</span>
                    <button
                      onClick={() => openFilterFor("Assigned To")}
                      className="p-1 rounded hover:bg-gray-100"
                      title="Filter Assigned To"
                    >
                      <Filter size={14} />
                    </button>
                    {columnFilters["Assigned To"]?.length > 0 && (
                      <span className="ml-1 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                        {columnFilters["Assigned To"].length}
                      </span>
                    )}
                  </div>
                </th>

                <th className="p-3">
                  <div className="flex items-center gap-2">
                    <span>Updated At</span>
                  </div>
                </th>

                <th className="p-3">
                  <div className="flex items-center gap-2">
                    <span>Status</span>
                    <button
                      onClick={() => openFilterFor("Status")}
                      className="p-1 rounded hover:bg-gray-100"
                      title="Filter Status"
                    >
                      <Filter size={14} />
                    </button>
                    {columnFilters["Status"]?.length > 0 && (
                      <span className="ml-1 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                        {columnFilters["Status"].length}
                      </span>
                    )}
                  </div>
                </th>

                <th className="p-3">
                  <div className="flex items-center gap-2">
                    <span>Priority</span>
                    <button
                      onClick={() => openFilterFor("Priority")}
                      className="p-1 rounded hover:bg-gray-100"
                      title="Filter Priority"
                    >
                      <Filter size={14} />
                    </button>
                    {columnFilters["Priority"]?.length > 0 && (
                      <span className="ml-1 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                        {columnFilters["Priority"].length}
                      </span>
                    )}
                  </div>
                </th>

                <th className="p-3">Action</th>
              </tr>
            </thead>

            <tbody>
              {currentRows.length === 0 ? (
                <tr>
                  <td colSpan="7" className="p-6 text-center text-gray-500">
                    No tasks found.
                  </td>
                </tr>
              ) : (
                currentRows.map((task) => (
                  <tr
                    key={task.taskNo}
                    className="border-b hover:bg-gray-50 transition-colors"
                  >
                    <td
                      className="p-3 font-medium text-blue-600 cursor-pointer hover:underline"
                      onClick={() => {
                        setSelectedtask(task);
                        setViewModal(true);
                      }}
                    >
                      {task.taskNo}
                    </td>
                    <td className="p-3">{task.projectName}</td>
                    <td className="p-3">{task.assignedTo || "--"}</td>
                    <td className="p-3">
                      {formatDateTime(task.lastUpdatedAt)}
                    </td>
                    <td className="p-3">{task.status}</td>
                    <td className="p-3">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${getSeverityStyle(
                          task.severity,
                        )}`}
                      >
                        {task.severity}
                      </span>
                    </td>
                    {/* ACTION BUTTON */}
                    <td className="p-3">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenActionMenu(
                            openActionMenu === task.id ? null : task.id,
                          );
                          setMenuPosition({ x: e.clientX, y: e.clientY });
                        }}
                        className="p-2 hover:bg-gray-100 rounded-full"
                      >
                        <MoreVertical size={18} />
                      </button>
                    </td>
                    {/* PORTAL DROPDOWN */}
                    {openActionMenu === task.id && (
                      <DropdownPortal>
                        <div
                          style={{
                            position: "fixed",
                            top: menuPosition.y + 10,
                            left: menuPosition.x - 160,
                            zIndex: 99999,
                          }}
                          className="w-44 bg-white border rounded-lg shadow-xl py-1"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {/* if status is Assigned */}
                          {task.status === "Assigned" && (
                            <>
                              <button
                                className="w-full text-left px-4 py-2 hover:bg-gray-100"
                                onClick={() => {
                                  setInProgressTaskData(task);
                                  setShowInProgressModal(true);
                                  setOpenActionMenu(null);
                                }}
                              >
                                In-Progress
                              </button>
                              <button
                                className="w-full text-left px-4 py-2 hover:bg-gray-100"
                                onClick={() => {
                                  setHoldTaskData(task);
                                  setShowHoldModal(true);
                                  setOpenActionMenu(null);
                                }}
                              >
                                Hold
                              </button>
                              {isPM && (
                                <button
                                  className="w-full text-left px-4 py-2 hover:bg-gray-100"
                                  onClick={() => {
                                    setReAssignTaskData(task);
                                    setShowReassignModal(true);
                                    setOpenActionMenu(null);
                                  }}
                                >
                                  Re-Assign Task
                                </button>
                              )}
                              {isPM && (
                                <button
                                  className="w-full text-left px-4 py-2 hover:bg-gray-100"
                                  onClick={() => {
                                    setInvalidTaskData(task);
                                    setShowInvalidTaskModal(true);
                                    setOpenActionMenu(null);
                                  }}
                                >
                                  Invalid Task
                                </button>
                              )}
                            </>
                          )}

                          {/* if status is In-Progress */}
                          {task.status === "In Progress" && (
                            <>
                              <button
                                className="w-full text-left px-4 py-2 hover:bg-gray-100"
                                onClick={() => {
                                  setCompletedTaskData(task);
                                  setShowCompletedModal(true);
                                  setOpenActionMenu(null);
                                }}
                              >
                                Mark Completed
                              </button>
                              <button
                                className="w-full text-left px-4 py-2 hover:bg-gray-100"
                                onClick={() => {
                                  setHoldTaskData(task);
                                  setShowHoldModal(true);
                                  setOpenActionMenu(null);
                                }}
                              >
                                Hold
                              </button>
                              {isPM && (
                                <button
                                  className="w-full text-left px-4 py-2 hover:bg-gray-100"
                                  onClick={() => {
                                    setReAssignTaskData(task);
                                    setShowReassignModal(true);
                                    setOpenActionMenu(null);
                                  }}
                                >
                                  Re-Assign Task
                                </button>
                              )}
                              {isPM && (
                                <button
                                  className="w-full text-left px-4 py-2 hover:bg-gray-100"
                                  onClick={() => {
                                    setInvalidTaskData(task);
                                    setShowInvalidTaskModal(true);
                                    setOpenActionMenu(null);
                                  }}
                                >
                                  Invalid Task
                                </button>
                              )}
                            </>
                          )}

                          {/* if status is Completed */}
                          {task.status === "Completed" && (
                            <>
                              <button
                                className="w-full text-left px-4 py-2 hover:bg-gray-100"
                                onClick={() => {
                                  setClosedTaskData(task);
                                  setShowClosedModal(true);
                                  setOpenActionMenu(null);
                                }}
                              >
                                Close
                              </button>
                              <button
                                className="w-full text-left px-4 py-2 hover:bg-gray-100"
                                onClick={() => {
                                  setReopenTaskData(task);
                                  setShowReopenModal(true);
                                  setOpenActionMenu(null);
                                }}
                              >
                                Re-open
                              </button>
                              <button
                                className="w-full text-left px-4 py-2 hover:bg-gray-100"
                                onClick={() => {
                                  setHoldTaskData(task);
                                  setShowHoldModal(true);
                                  setOpenActionMenu(null);
                                }}
                              >
                                Hold
                              </button>
                            </>
                          )}

                          {/* if status is Re-open */}
                          {task.status === "Re-open" && (
                            <>
                              <button
                                className="w-full text-left px-4 py-2 hover:bg-gray-100"
                                onClick={() => {
                                  setInProgressTaskData(task);
                                  setShowInProgressModal(true);
                                  setOpenActionMenu(null);
                                }}
                              >
                                In-Progress
                              </button>
                              <button
                                className="w-full text-left px-4 py-2 hover:bg-gray-100"
                                onClick={() => {
                                  setHoldTaskData(task);
                                  setShowHoldModal(true);
                                  setOpenActionMenu(null);
                                }}
                              >
                                Hold
                              </button>
                              {isPM && (
                                <button
                                  className="w-full text-left px-4 py-2 hover:bg-gray-100"
                                  onClick={() => {
                                    setReAssignTaskData(task);
                                    setShowReassignModal(true);
                                    setOpenActionMenu(null);
                                  }}
                                >
                                  Re-Assign Task
                                </button>
                              )}
                            </>
                          )}

                          {/* if status is Re-open */}
                          {task.status === "Hold" && (
                            <>
                              <button
                                className="w-full text-left px-4 py-2 hover:bg-gray-100"
                                onClick={() => {
                                  setReopenTaskData(task);
                                  setShowReopenModal(true);
                                  setOpenActionMenu(null);
                                }}
                              >
                                Re-open
                              </button>

                              <button
                                className="w-full text-left px-4 py-2 hover:bg-gray-100"
                                onClick={() => {
                                  setResumeTaskData(task);
                                  setShowResumeModal(true);
                                  setOpenActionMenu(null);
                                }}
                              >
                                Resume
                              </button>
                            </>
                          )}
                        </div>
                      </DropdownPortal>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={(page) => setCurrentPage(page)}
        />

        <CreateTaskModal
          show={showModal || openCreateFromCopy}
          onClose={() => {
            setShowModal(false);
            setOpenCreateFromCopy(false);
          }}
          onCreated={loadTasks}
        />

        <FilterTableModal
          show={filterModalOpen}
          onClose={() => setFilterModalOpen(false)}
          field={filterModalField}
          options={filterModalOptions}
          activeSelections={columnFilters[filterModalField] || []}
          onApply={applyColumnFilter}
          onResetAll={clearAllFilters}
        />

        <ViewTaskModal
          show={viewModal}
          onClose={() => setViewModal(false)}
          onOpenHistory={(history) => {
            setViewModal(false);
            setHistoryData(history);
            setHistoryModal(true);
          }}
          task={selectedtask}
          onInProgress={(task) => {
            setInProgressTaskData(task);
            setShowInProgressModal(true);
          }}
          onCompleted={(task) => {
            setCompletedTaskData(task);
            setShowCompletedModal(true);
          }}
          onReopen={(task) => {
            setReopenTaskData(task);
            setShowReopenModal(true);
          }}
          onResume={(task) => {
            setResumeTaskData(task);
            setShowResumeModal(true);
          }}
          onHold={(task) => {
            setHoldTaskData(task);
            setShowHoldModal(true);
          }}
          onClosed={(task) => {
            setClosedTaskData(task);
            setShowClosedModal(true);
          }}
          onReassign={(task) => {
            setReAssignTaskData(task);
            setShowReassignModal(true);
          }}
          onInvalid={(task) => {
            setInvalidTaskData(task);
            setShowInvalidTaskModal(true);
          }}
        />

        <TaskHistoryModal
          show={historyModal}
          onClose={() => {
            setHistoryModal(false);
            setViewModal(true);
          }}
          history={historyData}
        />

        <ADInProgressTaskModal
          show={showInProgressModal}
          onClose={() => setShowInProgressModal(false)}
          taskData={inProgressTaskData}
          onInProgress={loadTasks}
        />

        <ADCompletedTaskModal
          show={showCompletedModal}
          onClose={() => setShowCompletedModal(false)}
          taskData={completedTaskData}
          onCompleted={loadTasks}
        />

        <ADClosedTaskModal
          show={showClosedModal}
          onClose={() => setShowClosedModal(false)}
          taskData={closedTaskData}
          onClosed={loadTasks}
        />

        <ADReopenTaskModal
          show={showReopenModal}
          onClose={() => setShowReopenModal(false)}
          taskData={reopenTaskData}
          onReopen={loadTasks}
        />

        <ADResumeTaskModal
          show={showResumeModal}
          onClose={() => setShowResumeModal(false)}
          taskData={resumeTaskData}
          onResume={loadTasks}
        />

        <ADHoldTaskModal
          show={showHoldModal}
          onClose={() => setShowHoldModal(false)}
          taskData={holdTaskData}
          onHold={loadTasks}
        />

        <ReassignTaskModal
          show={showReassignModal}
          onClose={() => setShowReassignModal(false)}
          taskData={reAssignTaskData}
          onReassign={loadTasks}
        />

        <ADInvalidTaskModal
          show={showInvalidTaskModal}
          onClose={() => setShowInvalidTaskModal(false)}
          taskData={invalidTaskData}
          onInvalid={loadTasks}
        />
      </div>
      {/*  Notification Alert */}
      {alert && (
        <NotificationAlert
          type={alert.type}
          message={alert.message}
          show={true}
          duration={4000}
          onClose={() => setAlert(null)}
        />
      )}
    </>
  );
};

export default ADAllTask;
